import { useContext, useEffect, useState, type ReactNode } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MapOutlined, NavigationOutlined, ShareOutlined } from '@mui/icons-material';
import { MenuList, MenuItem, ListItemText, ListItemIcon, List, ListItem } from '@mui/material';

import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxUser } from '@/contexts/UserContext';
import { isValidLatLong } from '@/utilities/latlong';
import { openLocation, routeLocation } from '@/utilities/externalMapUtils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '@/components/common/TargetNameAndDescription';
import { MinimalTarget } from '@/generated/model';
import type { ExtraFeature } from '@/app/routing/guards/extraFeatures';

import './MenuPopover.css';
import CustomDrawer from '../common/CustomDrawer';
import CustomPopover from '../common/CustomPopover';

type MenuListItem = {
  key: string;
  content: ReactNode;
  onClick: () => void;
  canAppearWhenToken: boolean;
  requiredExtraFeature?: ExtraFeature;
};

export function MenuPopover({
  element,
  target,
  onClose,
}: {
  element: Element | null;
  target: MinimalTarget;
  onClose: (full: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ctx = useContext(ctxPluto);
  const userCtx = useContext(ctxUser);
  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => setVisible(isDrawerOpen));

    return () => cancelAnimationFrame(animationFrame);
  }, [isDrawerOpen]);

  function findTarget(id: number) {
    if (!ctx || !ctx.fullTargets) {
      return undefined;
    }

    return [...ctx.fullTargets].find((t) => t.target.id === id);
  }

  function onOpenExternalMap() {
    const t = findTarget(target.id);
    if (!t || !t.currentPosition || !isValidLatLong(t.currentPosition)) {
      return;
    }
    openLocation({ lat: t.currentPosition.latitude, lon: t.currentPosition.longitude, title: t.target.name });
    onClose(false);
  }

  function onRouteExternalMap() {
    const t = findTarget(target.id);
    if (!t || !t.currentPosition || !isValidLatLong(t.currentPosition)) {
      return;
    }
    routeLocation({ lat: t.currentPosition.latitude, lon: t.currentPosition.longitude, title: t.target.name });
    onClose(false);
  }

  function shareTarget() {
    navigate({
      pathname: './share',
      search: createSearchParams({
        targetId: String(target.id),
        targetName: target.name,
      }).toString(),
    });
    onClose(false);
  }

  const menuList = (size: 'medium' | 'small'): MenuListItem[] => [
    {
      key: 'tracking-menu-popover-open-external-map',
      content: (
        <>
          <ListItemIcon>
            <MapOutlined fontSize={size} />
          </ListItemIcon>
          <ListItemText>{t('targetMenu.locationOnMap')}</ListItemText>
        </>
      ),
      onClick: onOpenExternalMap,
      canAppearWhenToken: true,
    },
    {
      key: 'tracking-menu-popover-route-external-map',
      content: (
        <>
          <ListItemIcon>
            <NavigationOutlined fontSize={size} />
          </ListItemIcon>
          <ListItemText>{t('targetMenu.navigateOnMap')}</ListItemText>
        </>
      ),
      onClick: onRouteExternalMap,
      canAppearWhenToken: true,
    },
    {
      key: 'tracking-menu-popover-share-target',
      content: (
        <>
          <ListItemIcon>
            <ShareOutlined fontSize={size} />
          </ListItemIcon>
          <ListItemText>{t('targetMenu.sharableLink')}</ListItemText>
        </>
      ),
      onClick: shareTarget,
      canAppearWhenToken: false,
      requiredExtraFeature: 'extrafeature_pluto_target_share',
    },
  ];

  function handleClose() {
    setDrawerOpen(false);
    setTimeout(() => onClose(false), 300);
  }

  return isPhoneOrHighScreen() ? (
    <CustomDrawer isOpen={isVisible} onClose={handleClose} isDrawerOpen={isDrawerOpen}>
      <div className="tracking-popover-menu-header">
        <TargetNameAndDescription
          targetName={target.name}
          targetDescription={target.description}
          styleClassName={targetNameAndDescriptionStyle(undefined, undefined)}
        />
      </div>
      <List>
        {menuList('medium')
          .filter((item) => {
            const requiredExtraFeature = item.requiredExtraFeature;
            if (!requiredExtraFeature) {
              return true;
            }
            return userCtx?.hasExtraFeature(requiredExtraFeature);
          })
          .filter((item) => item.canAppearWhenToken || !userCtx?.isToken())
          .map((listItem) => (
            <ListItem key={listItem.key} onClick={listItem.onClick} data-qa-element-id={listItem.key}>
              {listItem.content}
            </ListItem>
          ))}
      </List>
    </CustomDrawer>
  ) : (
    <CustomPopover anchorEl={element} onClose={() => onClose(false)}>
      <MenuList>
        {menuList('small')
          .filter((item) => {
            const requiredExtraFeature = item.requiredExtraFeature;
            if (!requiredExtraFeature) {
              return true;
            }
            return userCtx?.hasExtraFeature(requiredExtraFeature);
          })
          .filter((item) => item.canAppearWhenToken || !userCtx?.isToken())
          .map((listItem) => (
            <MenuItem key={listItem.key} onClick={listItem.onClick} data-qa-element-id={listItem.key}>
              {listItem.content}
            </MenuItem>
          ))}
      </MenuList>
    </CustomPopover>
  );
}
